/* ==========================================================
   Lumia / VPP — <price-slider> Custom Element
   - Defines the missing custom element (your console showed it's undefined)
   - Syncs the 2 range inputs with the 2 number inputs
   - Updates slider UI parts if present:
     .inverse-left, .inverse-right, .price-slider-range, .price-slider-thumb
   - Prevents facet AJAX from firing on range "input" while dragging
     by stopping propagation on INPUT, but allowing CHANGE to bubble
   ========================================================== */

(() => {
  class PriceSlider extends HTMLElement {
    connectedCallback() {
      if (this.__vppInit) return;
      this.__vppInit = true;

      const ranges = this.querySelectorAll('input[type="range"]');
      if (!ranges || ranges.length < 2) return;

      this.minRange = ranges[0];
      this.maxRange = ranges[1];

      const numbers = this.querySelectorAll('input[type="number"]');
      // In Lumia price filter, there are usually 2 number inputs (From/To)
      this.minNumber = numbers && numbers.length ? numbers[0] : null;
      this.maxNumber = numbers && numbers.length > 1 ? numbers[1] : null;

      // Optional UI elements (present in many Lumia builds)
      this.inverseLeft = this.querySelector('.inverse-left');
      this.inverseRight = this.querySelector('.inverse-right');
      this.priceRange = this.querySelector('.price-slider-range');
      const thumbs = this.querySelectorAll('.price-slider-thumb');
      this.thumbMin = thumbs && thumbs.length ? thumbs[0] : null;
      this.thumbMax = thumbs && thumbs.length > 1 ? thumbs[1] : null;

      // Ensure inputs are interactable
      this.minRange.style.pointerEvents = 'auto';
      this.maxRange.style.pointerEvents = 'auto';

      // Initialize values from number inputs if they exist
      this.syncFromNumbersToRanges(true);
      this.updateUI();

      // ---- Range drag behavior ----
      // INPUT fires continuously while dragging.
      // We stop propagation so facet listeners don't AJAX-refresh mid-drag.
      this.minRange.addEventListener('input', (e) => {
        e.stopPropagation();
        this.coerceRanges();
        this.syncFromRangesToNumbers(false);
        this.updateUI();
      });

      this.maxRange.addEventListener('input', (e) => {
        e.stopPropagation();
        this.coerceRanges();
        this.syncFromRangesToNumbers(false);
        this.updateUI();
      });

      // CHANGE fires on release — allow it to bubble so facets apply.
      this.minRange.addEventListener('change', () => {
        this.coerceRanges();
        this.syncFromRangesToNumbers(true); // dispatch change on number inputs
        this.updateUI();
      });

      this.maxRange.addEventListener('change', () => {
        this.coerceRanges();
        this.syncFromRangesToNumbers(true);
        this.updateUI();
      });

      // ---- Number input behavior (typing From/To) ----
      if (this.minNumber) {
        this.minNumber.addEventListener('input', () => {
          this.syncFromNumbersToRanges(false);
          this.updateUI();
        });
        this.minNumber.addEventListener('change', () => {
          this.syncFromNumbersToRanges(false);
          this.updateUI();
        });
      }

      if (this.maxNumber) {
        this.maxNumber.addEventListener('input', () => {
          this.syncFromNumbersToRanges(false);
          this.updateUI();
        });
        this.maxNumber.addEventListener('change', () => {
          this.syncFromNumbersToRanges(false);
          this.updateUI();
        });
      }
    }

    getMinMax() {
      const min = this.toNum(this.minRange.min);
      const max = this.toNum(this.minRange.max);
      return { min, max };
    }

    toNum(v) {
      const n = parseFloat(String(v ?? '').replace(/,/g, ''));
      return Number.isFinite(n) ? n : 0;
    }

    clamp(v, min, max) {
      return Math.min(Math.max(v, min), max);
    }

    coerceRanges() {
      const { min, max } = this.getMinMax();
      let a = this.toNum(this.minRange.value);
      let b = this.toNum(this.maxRange.value);

      a = this.clamp(a, min, max);
      b = this.clamp(b, min, max);

      // Ensure min <= max
      if (a > b) a = b;

      this.minRange.value = String(a);
      this.maxRange.value = String(b);

      // Keep value attributes in sync (some Lumia CSS reads them)
      this.minRange.setAttribute('value', this.minRange.value);
      this.maxRange.setAttribute('value', this.maxRange.value);
    }

    syncFromRangesToNumbers(dispatchChange) {
      if (!this.minNumber || !this.maxNumber) return;

      const a = this.toNum(this.minRange.value);
      const b = this.toNum(this.maxRange.value);

      this.minNumber.value = String(a);
      this.maxNumber.value = String(b);

      if (dispatchChange) {
        // Let facets apply on release (numbers usually carry the filter param names)
        this.minNumber.dispatchEvent(new Event('change', { bubbles: true }));
        this.maxNumber.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncFromNumbersToRanges(initial) {
      const { min, max } = this.getMinMax();

      if (!this.minNumber || !this.maxNumber) {
        // If no number fields exist, still coerce ranges and update UI
        this.coerceRanges();
        return;
      }

      let a = this.minNumber.value === '' ? this.toNum(this.minRange.value) : this.toNum(this.minNumber.value);
      let b = this.maxNumber.value === '' ? this.toNum(this.maxRange.value) : this.toNum(this.maxNumber.value);

      a = this.clamp(a, min, max);
      b = this.clamp(b, min, max);
      if (a > b) a = b;

      this.minRange.value = String(a);
      this.maxRange.value = String(b);

      this.minRange.setAttribute('value', this.minRange.value);
      this.maxRange.setAttribute('value', this.maxRange.value);

      if (initial) {
        // If numbers were blank, populate them from ranges
        if (this.minNumber.value === '') this.minNumber.value = String(a);
        if (this.maxNumber.value === '') this.maxNumber.value = String(b);
      }
    }

    updateUI() {
      const { min, max } = this.getMinMax();
      const a = this.toNum(this.minRange.value);
      const b = this.toNum(this.maxRange.value);

      const span = Math.max(max - min, 1);
      const pA = ((a - min) / span) * 100;
      const pB = ((b - min) / span) * 100;

      // Update thumbs if present
      if (this.thumbMin) this.thumbMin.style.left = `${pA}%`;
      if (this.thumbMax) this.thumbMax.style.left = `${pB}%`;

      // Update active range bar if present
      if (this.priceRange) {
        this.priceRange.style.left = `${pA}%`;
        this.priceRange.style.right = `${100 - pB}%`;
      }

      // Update inverse segments if present
      if (this.inverseLeft) this.inverseLeft.style.width = `${pA}%`;
      if (this.inverseRight) this.inverseRight.style.width = `${100 - pB}%`;
    }
  }

  // Define only if missing
  if (!customElements.get('price-slider')) {
    customElements.define('price-slider', PriceSlider);
  }
})();
